import React from "react";
import { gettext } from "i18n";
import { gql, useQuery } from "@apollo/client";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import FormControl from "react-bootstrap/FormControl";
import {
  GetDatasourcesByStation,
  GetDatasourcesByStationVariables,
  GetDatasourcesByStation_datasource,
} from "utils/__generated__/GetDatasourcesByStation";
import {
  GET_DATASOURCES_BY_STATION,
  getSourceUnitSymbol,
} from "utils/stations";
import { GetUnits } from "utils/__generated__/GetUnits";
import { GET_UNITS } from "utils/units";
import { sortByTranslatedKey } from "utils/arrays";
import { SearchableStationPicker } from "components/common";
import { SearchableCheckboxDropdown } from "components/common/SearchableDropdown";
import { GetDatasourceData_source_data } from "./__generated__/GetDatasourceData";

/**
 * Dashboard widget related queries and utilities
 */

export const DASHBOARD_TYPE_REGULAR = 1;
export const DASHBOARD_TYPE_REPORTING = 2;

export interface SourceVisualSettings {
  sourceUUID: string;
  fontSize: number;
  fontColor: string;
  lineColor: string;
  axisScaleManual?: boolean;
  axisScaleMin?: number;
  axisScaleMax?: number;
  showStationName?: boolean;
  showStationId?: boolean;
  showSourceName?: boolean;
  showUnit?: boolean;
  limitWarningLow?: number | null;
  limitCriticalLow?: number | null;
  limitWarningHigh?: number | null;
  limitCriticalHigh?: number | null;
}

export const formatDatasourceLabel = (
  source: {
    name?: string | null;
    type: string | null;
    unit?: { symbol: string } | null;
  },
  station:
    | { name?: string | null; identifier?: number | string | null }
    | null
    | undefined,
  settings?: Pick<
    SourceVisualSettings,
    "showStationName" | "showStationId" | "showSourceName" | "showUnit"
  >,
) => {
  const showStationName = settings?.showStationName ?? true;
  const showStationId = settings?.showStationId ?? true;
  const showSourceName = settings?.showSourceName ?? true;
  const showUnit = settings?.showUnit ?? true;

  const stationParts: string[] = [];
  if (showStationName && station?.name) {
    stationParts.push(station.name);
  }
  if (showStationId && station?.identifier != null) {
    stationParts.push(`(${station.identifier})`);
  }
  const stationPart = stationParts.join(" ");

  const sourceParts: string[] = [];
  if (showSourceName && source?.name) {
    sourceParts.push(source.name);
  }
  const unitSymbol = getSourceUnitSymbol(source);
  if (showUnit && unitSymbol) {
    sourceParts.push(unitSymbol);
  }
  const sourcePart = sourceParts.join(" ");

  const label = [stationPart, sourcePart]
    .filter((part) => part.length > 0)
    .join(" - ");

  // If every name component is hidden, fall back to the data source name so
  // the label never renders as an empty string.
  return label || source?.name || "";
};

export interface ChartVisualSettings {
  axisScaleManual: boolean;
  axisScaleMin: number;
  axisScaleMax: number;
  lineColors: string[];
  fontSize: number;
  fontColor: string;
  sourceVisualSettings?: SourceVisualSettings[];
}

export interface TableVisualSettings {
  fontSize: number;
  fontColor: string;
  headerFontSize: number;
  headerBackgroundColor: string;
}

export interface CommentWidgetMetaData {
  title: string;
  content: string;
  fontSize: number;
  fontColor: string;
  backgroundColor: string;
}

export type VisualSettings = ChartVisualSettings | TableVisualSettings;

export const DEFAULT_CHART_VISUAL_SETTINGS: ChartVisualSettings = {
  axisScaleManual: false,
  axisScaleMin: 0,
  axisScaleMax: 100,
  lineColors: [],
  fontSize: 12,
  fontColor: "#333333",
  sourceVisualSettings: [],
};

export const DEFAULT_TABLE_VISUAL_SETTINGS: TableVisualSettings = {
  fontSize: 14,
  fontColor: "#333333",
  headerFontSize: 14,
  headerBackgroundColor: "#f8f9fa",
};

export type MetaDataFieldType = "text" | "color" | "number" | "textarea";

export interface MetaDataField {
  type: MetaDataFieldType;
  placeholder?: string;
  min?: number;
  max?: number;
  rows?: number;
}

export interface WidgetTemplate {
  key: string;
  name: string;
  adminOnly?: boolean;
  metaData: Record<string, any>;
  metaDataFields?: Record<string, MetaDataField>;
  metaDataParsers?: Record<string, (raw: any) => any>;
  pickers?: PickerTemplateConfig[] | Record<string, any>;
  requiredFields?: string[];
  supportsMultipleSources?: boolean;
  visualSettings?: VisualSettings;
  visualSettingsType?: "chart" | "table" | "none";
  availableFor?: number[];
  allowExcelExport?: boolean;
}

export interface PickerTemplateConfig {
  field: string;
  label: string | null;
  key: string;
  filters?: Record<string, (item: any) => boolean>;
  params?: Record<string, any>;
}

// Total discharge tables of different periods only differ by key and name
const dischargeTableTemplate = (key: string, name: string): WidgetTemplate => ({
  key,
  name,
  metaData: {
    title: "",
  },
  metaDataParsers: {},
  pickers: [
    {
      field: "displayUnit",
      label: "Display unit",
      key: "unit_picker",
      filters: {
        displayUnit: (unit: any) => {
          if (!unit) {
            return false;
          }
          const allowed_units = ["l", "m³"];
          return allowed_units.includes(unit.symbol);
        },
      },
    },
    {
      field: "sourceUUIDs",
      label: null,
      filters: {
        datasource: (datasource: GetDatasourcesByStation_datasource) => {
          if (!datasource.unit) {
            return false;
          }
          const allowed_units = ["l/s"];
          return allowed_units.includes(datasource.unit.symbol);
        },
      },
      key: "station_multi_source_picker",
    },
  ],
  availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
});

export const WIDGET_TEMPLATES: WidgetTemplate[] = [
  {
    key: "iframe_widget",
    name: "Embed content (widget)",
    metaData: {
      title: "iframe Widget",
      sourceUrl: "https://www.youtube.com/embed/1O1kDmfUG68",
      aspectWidth: 16,
      aspectHeight: 9,
    },
    metaDataParsers: {
      aspectWidth: (raw: string) => Number(raw),
      aspectHeight: (raw: string) => Number(raw),
      maxWidth: (raw: string) => Number(raw),
    },
    pickers: [],
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "burrel_camera_widget",
    name: "Burrel camera (widget)",
    metaData: {
      title: "Camera",
      cameraId: "",
    },
    metaDataParsers: {
      cameraId: (raw: string) => Number(raw),
    },
    pickers: [
      {
        field: "cameraId",
        label: "Camera",
        key: "burrel_camera_picker",
      },
    ],
    requiredFields: ["cameraId"],
    visualSettingsType: "none",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "primary_parameter_chart",
    name: "Primary parameter (chart)",
    metaData: {
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [],
    visualSettingsType: "chart",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "data_source_chart",
    name: "Optional combination (chart)",
    metaData: {
      title: "",
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [
      {
        field: "sourceUUIDs",
        label: null,
        key: "station_multi_source_picker",
      },
    ],
    supportsMultipleSources: true,
    visualSettingsType: "chart",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "manual_lab_data_table",
    name: "Manual/lab data (table)",
    metaData: {
      title: "",
      layoutOption: "station_params_rows",
    },
    metaDataParsers: {},
    pickers: [
      {
        field: "sourceUUIDs",
        label: null,
        key: "station_multi_source_picker",
        filters: {
          datasource: (datasource: any) => {
            if (!datasource?.type) {
              return false;
            }
            return ["manual_point_set"].includes(datasource.type);
          },
        },
      },
      {
        field: "layoutOption",
        label: "Layout options",
        key: "manual_lab_data_layout_picker",
      },
    ],
    supportsMultipleSources: true,
    visualSettingsType: "table",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "stats_summary_widget",
    name: "Statistics summary (widget)",
    metaData: {
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [],
    visualSettingsType: "none",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "primary_parameter_average_chart",
    name: "Primary parameter min&max&avg (chart)",
    metaData: {
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [],
    visualSettingsType: "chart",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "daily_averages_table",
    name: "Primary parameter min&max&avg (table)",
    metaData: {
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [],
    visualSettingsType: "table",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "daily_averages_table_multi_source",
    name: "Daily averages (table)",
    metaData: {
      title: "",
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [
      {
        field: "sourceUUIDs",
        label: null,
        key: "station_multi_source_picker",
      },
    ],
    supportsMultipleSources: true,
    visualSettingsType: "table",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "temperature_profile_widget",
    name: "Temperature profile (widget)",
    metaData: {},
    metaDataParsers: {
      // Make sure all entered stationIds are numbers
      addStationIds: (raw: string[]) => {
        return raw.map((r) => Number(r));
      },
    },
    pickers: [
      {
        field: "addStationIds",
        label: null,
        key: "multi_station_picker",
      },
    ],
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  dischargeTableTemplate(
    "total_hourly_discharge_table",
    "Total hourly discharge (table)",
  ),
  dischargeTableTemplate(
    "total_daily_discharge_table",
    "Total daily discharge (table)",
  ),
  {
    key: "daily_averages_multi_parameter",
    name: "Daily averages (chart)",
    metaData: {
      title: "",
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [
      {
        field: "sourceUUIDs",
        label: null,
        key: "station_multi_source_picker",
      },
    ],
    supportsMultipleSources: true,
    visualSettingsType: "chart",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  dischargeTableTemplate(
    "total_monthly_discharge_table",
    "Total monthly discharge (table)",
  ),
  dischargeTableTemplate(
    "total_annual_discharge_table",
    "Total annual discharge (table)",
  ),
  {
    key: "latest_value_exporter",
    name: "Latest Value Exporter Hydrotempo",
    adminOnly: true,
    metaData: {
      title: "",
      stationCode: "",
      emailSubject: "Hetkellinen",
      sendToEmailAddress: "recipient@example.com",
      intervalMinutes: 60,
    },
    metaDataParsers: {},
    requiredFields: ["stationCode", "sendToEmailAddress", "intervalMinutes"],
    pickers: [
      {
        field: "sourceUUIDs",
        label: null,
        key: "station_multi_source_picker",
        params: {
          showUnitMapping: true,
        },
      },
    ],
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "accrued_difference_chart",
    name: "Accrued difference (chart)",
    metaData: {
      title: "",
      ignoreZeroValues: false,
    },
    metaDataParsers: {},
    pickers: [
      {
        field: "sourceUUIDs",
        label: null,
        key: "station_multi_source_picker",
      },
    ],
    supportsMultipleSources: true,
    visualSettingsType: "chart",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "comment_widget",
    name: "Text comment",
    metaData: {
      title: "",
      content: "",
      fontSize: 14,
      fontColor: "#333333",
      backgroundColor: "#ffffff",
    },
    metaDataFields: {
      content: { type: "textarea", rows: 5 },
      fontSize: { type: "number", min: 8, max: 32 },
      fontColor: { type: "color" },
      backgroundColor: { type: "color" },
    },
    metaDataParsers: {
      fontSize: (raw: string) => Number(raw),
    },
    pickers: [],
    visualSettingsType: "none",
    availableFor: [DASHBOARD_TYPE_REGULAR, DASHBOARD_TYPE_REPORTING],
  },
  {
    key: "load_calculation_widget",
    name: "Load calculation",
    availableFor: [DASHBOARD_TYPE_REPORTING],
    metaData: {
      title: "",
      cacheDurationHours: 24,
      reportingFrequency: "month",
      showResultsIn: "table",
      flowDataSourceUUID: "",
      flowUnit: "l",
      numberOfLoadParameters: 1,
      loadParameters: [],
      dateRange: [
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        new Date().toISOString(),
      ],
    },
    metaDataFields: {
      cacheDurationHours: { type: "number", min: 1, max: 168 },
      numberOfLoadParameters: { type: "number", min: 1, max: 20 },
    },
    metaDataParsers: {
      cacheDurationHours: (raw: string) => Number(raw),
      numberOfLoadParameters: (raw: string) => Number(raw),
    },
    pickers: [
      {
        field: "flowDataSourceUUID",
        label: "Flow data source",
        key: "flow_source_picker",
        filters: {
          datasource: (datasource: GetDatasourcesByStation_datasource) => {
            if (!datasource.unit) {
              return false;
            }
            return ["l/s"].includes(datasource.unit.symbol);
          },
        },
      },
      {
        field: "flowUnit",
        label: "Flow unit",
        key: "unit_picker",
        filters: {
          displayUnit: (unit: any) => {
            if (!unit) return false;
            return ["l", "m³"].includes(unit.symbol);
          },
        },
      },
      {
        field: "reportingFrequency",
        label: "Reporting frequency",
        key: "reporting_frequency_picker",
      },
      {
        field: "showResultsIn",
        label: "Show results in",
        key: "show_results_picker",
      },
      {
        field: "loadParameters",
        label: null,
        key: "load_parameters_picker",
      },
    ],
    requiredFields: ["flowDataSourceUUID", "flowUnit"],
    visualSettingsType: "none",
    allowExcelExport: true,
  },
];

export const STATIC_DATE_WIDGETS: string[] = [
  "comment_widget",
  "iframe_widget",
  "burrel_camera_widget",
];

export interface DatasourcePickerProps {
  onChange: (e: any) => void;
  value: string;
  editedValues?: Record<string, any>;
}
export const DatasourcePicker = ({
  editedValues,
  onChange,
  value,
}: DatasourcePickerProps) => {
  const { metaData } = editedValues ?? {};
  const { stationId } = metaData ?? {};
  const { data: dataSourceData } = useQuery<
    GetDatasourcesByStation,
    GetDatasourcesByStationVariables
  >(GET_DATASOURCES_BY_STATION, {
    variables: {
      stationId: Number(stationId),
    },
    skip: !stationId,
  });

  if (!stationId) {
    return <></>;
  }

  const datasourceList = dataSourceData?.datasource;
  return (
    <Form.Select
      aria-label={gettext("Data Source")}
      onChange={onChange}
      value={value ?? ""}
    >
      <option value="">Please select a data source</option>
      {sortByTranslatedKey(datasourceList, "name")?.map((datasource) => (
        <option
          key={`datasource_picker_${datasource.source_uuid}`}
          value={String(datasource.source_uuid)}
        >
          {datasource.name}
          {datasource.unit?.symbol ? ` (${datasource.unit.symbol})` : ""}
        </option>
      ))}
    </Form.Select>
  );
};

export interface FlowSourcePickerProps {
  onChange: (e: any) => void;
  value: string;
  editedValues?: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  filters?: Record<string, (item: any) => boolean>;
}
export const FlowSourcePicker = ({
  onChange,
  value,
  editedValues,
  setEditedValues,
  filters,
}: FlowSourcePickerProps) => {
  const [selectedStationId, setSelectedStationId] = React.useState<string>(
    editedValues?.metaData?.selectedFlowStationId ?? "",
  );
  const { data: dataSourceData } = useQuery<
    GetDatasourcesByStation,
    GetDatasourcesByStationVariables
  >(GET_DATASOURCES_BY_STATION, {
    variables: {
      stationId: Number(selectedStationId),
    },
    skip: !selectedStationId,
  });

  const datasourceFilter = filters?.datasource ?? (() => true);
  const datasourceList = dataSourceData?.datasource?.filter(datasourceFilter);

  return (
    <>
      <div className="mb-2">
        <Form.Label className="small text-muted">
          {gettext("Station")}
        </Form.Label>
        <SearchableStationPicker
          onChange={(newStationId) => {
            setSelectedStationId(newStationId);
            onChange({ target: { value: "" } });
          }}
          value={selectedStationId}
        />
      </div>
      {selectedStationId && (
        <div>
          <Form.Label className="small text-muted">
            {gettext("Data Source")}
          </Form.Label>
          <Form.Select
            aria-label={gettext("Flow Data Source")}
            onChange={(e: any) => {
              const selectedUUID = e.target.value;
              onChange({ target: { value: selectedUUID } });
              if (setEditedValues && editedValues) {
                setEditedValues({
                  ...editedValues,
                  metaData: {
                    ...editedValues.metaData,
                    flowDataSourceUUID: selectedUUID,
                    selectedFlowStationId: selectedStationId,
                  },
                });
              }
            }}
            value={value ?? ""}
          >
            <option value="">{gettext("Please select a data source")}</option>
            {sortByTranslatedKey(datasourceList, "name")?.map((datasource) => (
              <option
                key={`flow_source_picker_${datasource.source_uuid}`}
                value={String(datasource.source_uuid)}
              >
                {datasource.name}
                {datasource.unit?.symbol ? ` (${datasource.unit.symbol})` : ""}
              </option>
            ))}
          </Form.Select>
        </div>
      )}
    </>
  );
};

export interface LoadParametersPickerProps {
  editedValues: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

export const LoadParametersPicker = ({
  editedValues,
  setEditedValues,
}: LoadParametersPickerProps) => {
  const numberOfParameters =
    editedValues?.metaData?.numberOfLoadParameters ?? 1;
  const loadParameters = editedValues?.metaData?.loadParameters ?? [];

  const updateLoadParameter = (index: number, field: string, value: any) => {
    const newLoadParameters = [...loadParameters];
    if (!newLoadParameters[index]) {
      newLoadParameters[index] = {
        name: "",
        concentrationDataSourceUUID: "",
        calculationMethod: 1,
        detectionLimit: 0,
        belowDetectionLimitHandling: 1,
        missingValuesHandling: 1,
      };
    }
    newLoadParameters[index] = {
      ...newLoadParameters[index],
      [field]: value,
    };
    setEditedValues({
      ...editedValues,
      metaData: {
        ...editedValues.metaData,
        loadParameters: newLoadParameters,
      },
    });
  };

  return (
    <tr>
      <td colSpan={2} className="p-0">
        <div className="load-parameters-container">
          {Array.from({ length: numberOfParameters }).map((_, index) => (
            <LoadParameterRow
              key={`load_param_${index}`}
              index={index}
              parameter={loadParameters[index]}
              onChange={(field, value) =>
                updateLoadParameter(index, field, value)
              }
            />
          ))}
        </div>
      </td>
    </tr>
  );
};

interface LoadParameterRowProps {
  index: number;
  parameter?: {
    name: string;
    concentrationDataSourceUUID: string;
    calculationMethod: number;
    detectionLimit: number;
    belowDetectionLimitHandling: number;
    missingValuesHandling: number;
    selectedStationId?: string;
  };
  onChange: (field: string, value: any) => void;
}

const LoadParameterRow = ({
  index,
  parameter,
  onChange,
}: LoadParameterRowProps) => {
  const param = parameter ?? {
    name: "",
    concentrationDataSourceUUID: "",
    calculationMethod: 1,
    detectionLimit: 0,
    belowDetectionLimitHandling: 1,
    missingValuesHandling: 1,
    unit: "mg/l",
    selectedStationId: "",
  };

  return (
    <div className="load-parameter-block">
      <div className="load-parameter-header">
        {gettext("Load Parameter")} #{index + 1}
      </div>
      <div className="load-parameter-content">
        <div className="load-parameter-row">
          <label className="load-parameter-label">{gettext("Name")}</label>
          <div className="load-parameter-input">
            <Form.Control
              type="text"
              value={param.name}
              placeholder={gettext("e.g., TSS, N, P")}
              onChange={(e: any) => onChange("name", e.target.value)}
            />
          </div>
        </div>
        <div className="load-parameter-row">
          <label className="load-parameter-label">
            {gettext("Concentration data source")}
          </label>
          <div className="load-parameter-input">
            <ConcentrationSourcePicker
              value={param.concentrationDataSourceUUID}
              selectedStationId={param.selectedStationId}
              onChange={(value, stationId) => {
                onChange("concentrationDataSourceUUID", value);
                if (stationId) {
                  onChange("selectedStationId", stationId);
                }
              }}
            />
          </div>
        </div>
        <div className="load-parameter-row">
          <label className="load-parameter-label">
            {gettext("Calculation method")}
          </label>
          <div className="load-parameter-input">
            <Form.Select
              value={param.calculationMethod}
              onChange={(e: any) =>
                onChange("calculationMethod", Number(e.target.value))
              }
            >
              <option value={1}>{gettext("Flow-weighted average")}</option>
            </Form.Select>
          </div>
        </div>
        <div className="load-parameter-row">
          <label className="load-parameter-label">
            {gettext("Detection limit")}
          </label>
          <div className="load-parameter-input">
            <Form.Control
              type="number"
              value={param.detectionLimit}
              placeholder={gettext("Detection limit value")}
              onChange={(e: any) =>
                onChange("detectionLimit", Number(e.target.value))
              }
            />
          </div>
        </div>
        <div className="load-parameter-row">
          <label className="load-parameter-label">
            {gettext("Results below detection limit calculated as")}
          </label>
          <div className="load-parameter-input">
            <Form.Select
              value={param.belowDetectionLimitHandling}
              onChange={(e: any) =>
                onChange("belowDetectionLimitHandling", Number(e.target.value))
              }
            >
              <option value={1}>
                {gettext("50% of the detection limit value")}
              </option>
              <option value={2}>{gettext("Zero")}</option>
            </Form.Select>
          </div>
        </div>
        <div className="load-parameter-row">
          <label className="load-parameter-label">
            {gettext("Missing values calculated as")}
          </label>
          <div className="load-parameter-input">
            <Form.Select
              value={param.missingValuesHandling}
              onChange={(e: any) =>
                onChange("missingValuesHandling", Number(e.target.value))
              }
            >
              <option value={1}>
                {gettext("Average from next and previous")}
              </option>
              <option value={2}>{gettext("Skip")}</option>
            </Form.Select>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ConcentrationSourcePickerProps {
  value: string;
  selectedStationId?: string;
  onChange: (value: string, stationId?: string) => void;
}

const ConcentrationSourcePicker = ({
  value,
  selectedStationId: initialStationId,
  onChange,
}: ConcentrationSourcePickerProps) => {
  const [selectedStationId, setSelectedStationId] = React.useState<string>(
    initialStationId ?? "",
  );
  const { data: dataSourceData } = useQuery<
    GetDatasourcesByStation,
    GetDatasourcesByStationVariables
  >(GET_DATASOURCES_BY_STATION, {
    variables: {
      stationId: Number(selectedStationId),
    },
    skip: !selectedStationId,
  });

  return (
    <>
      <div className="mb-2">
        <SearchableStationPicker
          onChange={(newStationId) => {
            setSelectedStationId(newStationId);
            onChange("", newStationId);
          }}
          value={selectedStationId}
        />
      </div>
      {selectedStationId && (
        <Form.Select
          value={value}
          onChange={(e: any) => onChange(e.target.value, selectedStationId)}
        >
          <option value="">{gettext("Please select a data source")}</option>
          {sortByTranslatedKey(dataSourceData?.datasource, "name")?.map(
            (datasource) => (
              <option
                key={`conc_source_${datasource.source_uuid}`}
                value={String(datasource.source_uuid)}
              >
                {datasource.name}
                {datasource.unit?.symbol ? ` (${datasource.unit.symbol})` : ""}
              </option>
            ),
          )}
        </Form.Select>
      )}
    </>
  );
};

export interface MultiSourcePickerRowProps {
  field: string;
  value: string;
  filters?: Record<string, (item: any) => boolean>;
  rowIndex: number;
  editedValues: Record<string, any>;
  showUnitMapping?: boolean;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  selectedStationIds: string[];
  setSelectedStationIds: React.Dispatch<React.SetStateAction<string[]>>;
}
export const MultiSourcePickerRow = ({
  rowIndex,
  field,
  value,
  filters,
  editedValues,
  showUnitMapping,
  setEditedValues,
  selectedStationIds,
  setSelectedStationIds,
}: MultiSourcePickerRowProps) => {
  const stationId = selectedStationIds[rowIndex];
  const { data: dataSourceData } = useQuery<
    GetDatasourcesByStation,
    GetDatasourcesByStationVariables
  >(GET_DATASOURCES_BY_STATION, {
    variables: {
      stationId: Number(stationId),
    },
    skip: !stationId,
  });

  const datasourceList = dataSourceData?.datasource;
  const existingValues = editedValues?.metaData?.[field] ?? [];
  const existingUnitMapping = editedValues?.metaData?.unitMapping ?? {};
  const currentSourceUUID = existingValues[rowIndex];
  const currentUnitMapping = existingUnitMapping?.[currentSourceUUID];
  const { unitLabel, unitModifier } = currentUnitMapping ?? {};

  const datasourceFilter = filters?.datasource ?? (() => true);
  return (
    <>
      <tr>
        <th colSpan={2}>
          <h5>
            {gettext("Data Source")} #{rowIndex + 1}{" "}
            {(rowIndex > 0 && gettext("(optional)")) || ""}
          </h5>
        </th>
      </tr>
      <tr>
        <th>{gettext("Source Station")}</th>
        <td>
          <SearchableStationPicker
            onChange={(newStationId) => {
              const newSelectedStationIds = [...selectedStationIds];
              newSelectedStationIds[rowIndex] = newStationId;
              setSelectedStationIds(newSelectedStationIds);
            }}
            value={stationId}
          />
        </td>
      </tr>
      {!!stationId && (
        <>
          <tr>
            <th>{gettext("Data Source")}</th>
            <td>
              <Form.Select
                aria-label={gettext("Data Source")}
                onChange={(e: any) => {
                  const value = e.target.value;
                  const newExistingValues = [...existingValues];
                  newExistingValues.splice(rowIndex, 1, value);
                  setEditedValues({
                    ...editedValues,
                    metaData: {
                      ...editedValues.metaData,
                      [field]: newExistingValues,
                    },
                  });
                }}
                value={currentSourceUUID ?? ""}
              >
                <option value="">Please select a data source</option>
                {sortByTranslatedKey(datasourceList, "name")
                  .filter(datasourceFilter)
                  .map((datasource) => (
                    <option
                      key={`datasource_picker_${datasource.source_uuid}`}
                      value={String(datasource.source_uuid)}
                    >
                      {datasource.name}
                      {datasource.unit?.symbol
                        ? ` (${datasource.unit.symbol})`
                        : ""}
                    </option>
                  ))}
              </Form.Select>
            </td>
          </tr>
          {showUnitMapping && !!currentSourceUUID && (
            <>
              <tr>
                <th>{gettext("Render unit as")}</th>
                <td>
                  <FormControl
                    type="text"
                    value={unitLabel}
                    placeholder={gettext("W, Q, T or m³/s etc. (optional)")}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      setEditedValues({
                        ...editedValues,
                        metaData: {
                          ...editedValues.metaData,
                          unitMapping: {
                            ...existingUnitMapping,
                            [currentSourceUUID]: {
                              ...existingUnitMapping[currentSourceUUID],
                              unitLabel: value,
                            },
                          },
                        },
                      });
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>{gettext("Value modifier")}</th>
                <td>
                  <FormControl
                    type="text"
                    value={unitModifier ?? 1}
                    placeholder={gettext("(optional, defaults to 1 if empty)")}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      setEditedValues({
                        ...editedValues,
                        metaData: {
                          ...editedValues.metaData,
                          unitMapping: {
                            ...existingUnitMapping,
                            [currentSourceUUID]: {
                              ...existingUnitMapping[currentSourceUUID],
                              unitModifier: value,
                            },
                          },
                        },
                      });
                    }}
                  />
                </td>
              </tr>
            </>
          )}
        </>
      )}
    </>
  );
};

export interface MultiSourcePickerProps {
  field: string;
  value: string;
  editedValues: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  filters?: Record<string, (item: any) => boolean>;
  params?: Record<string, any>;
}
export const MultiSourcePicker = ({
  field,
  value,
  editedValues,
  setEditedValues,
  filters,
  params,
}: MultiSourcePickerProps) => {
  const { metaData } = editedValues ?? {};
  const { sourceUUIDs, selectedStationIds: initialStationIds } = metaData ?? {};
  const { showUnitMapping } = params ?? {};

  const [selectedStationIds, setSelectedStationIds] = React.useState<string[]>(
    initialStationIds?.map(String) ?? [],
  );

  React.useEffect(() => {
    if (initialStationIds && initialStationIds.length > 0) {
      setSelectedStationIds(initialStationIds.map(String));
    }
  }, [initialStationIds]);

  const pickerCount = (sourceUUIDs?.length ?? 0) + 1;

  return (
    <>
      {Array.from(Array(pickerCount).keys()).map((index) => (
        <MultiSourcePickerRow
          key={`multi_source_picker_row_${index}`}
          field={field}
          value={value}
          filters={filters}
          rowIndex={index}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
          selectedStationIds={selectedStationIds}
          setSelectedStationIds={setSelectedStationIds}
          showUnitMapping={showUnitMapping}
        />
      ))}
    </>
  );
};

export interface MultiStationPickerRowProps {
  field: string;
  value: string;
  rowIndex: number;
  editedValues: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}
export const MultiStationPickerRow = ({
  rowIndex,
  field,
  value,
  editedValues,
  setEditedValues,
}: MultiStationPickerRowProps) => {
  const existingValues = editedValues?.metaData?.[field] ?? [];
  const stationId = String(existingValues[rowIndex]) ?? "";
  return (
    <>
      <tr>
        <th colSpan={2}>
          <h5>
            {gettext("Source Station")} #{rowIndex + 1}{" "}
            {(rowIndex > 0 && gettext("(optional)")) || ""}
          </h5>
        </th>
      </tr>
      <tr>
        <th>{gettext("Station")}</th>
        <td>
          <SearchableStationPicker
            onChange={(value) => {
              const newValues = [...existingValues];
              newValues.splice(rowIndex, 1, value);
              setEditedValues({
                ...editedValues,
                metaData: {
                  ...editedValues.metaData,
                  [field]: newValues,
                },
              });
            }}
            value={stationId}
          />
        </td>
      </tr>
    </>
  );
};

export interface MultiStationPickerProps {
  field: string;
  value: string;
  editedValues: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}
export const MultiStationPicker = ({
  field,
  value,
  editedValues,
  setEditedValues,
}: MultiStationPickerProps) => {
  const { metaData } = editedValues ?? {};
  const { addStationIds } = metaData ?? {};

  const pickerCount = (addStationIds?.length ?? 0) + 1;
  return (
    <>
      {Array.from(Array(pickerCount).keys()).map((index) => (
        <MultiStationPickerRow
          key={`multi_station_picker_row_${index}`}
          field={field}
          value={value}
          rowIndex={index}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
        />
      ))}
    </>
  );
};

const GET_BURREL_CAMERAS_FOR_PICKER = gql`
  query GetBurrelCamerasForPicker {
    burrel_camera(order_by: { id: asc }) {
      id
      name
    }
  }
`;

export interface BurrelCameraPickerProps {
  onChange: (e: any) => void;
  value: string;
}
export const BurrelCameraPicker = ({
  onChange,
  value,
}: BurrelCameraPickerProps) => {
  const { data } = useQuery<{
    burrel_camera: { id: number; name: string }[];
  }>(GET_BURREL_CAMERAS_FOR_PICKER);

  const cameras = data?.burrel_camera ?? [];
  return (
    <Form.Select
      aria-label={gettext("Camera")}
      onChange={onChange}
      value={value ?? ""}
    >
      <option value="">{gettext("Please select a camera")}</option>
      {cameras.map((camera) => (
        <option key={`burrel_camera_${camera.id}`} value={String(camera.id)}>
          {camera.id} - {camera.name}
        </option>
      ))}
    </Form.Select>
  );
};

export interface UnitPickerProps {
  onChange: (e: any) => void;
  value: string;
  filters?: Record<string, (item: any) => boolean>;
}
export const UnitPicker = ({ onChange, value, filters }: UnitPickerProps) => {
  const { data: unitQuery } = useQuery<GetUnits>(GET_UNITS);

  const filter = filters?.displayUnit ?? (() => true);
  const unitList = unitQuery?.unit ? [...unitQuery?.unit] : [];
  unitList.sort((a, b) => (a.id > b.id ? 1 : -1));
  const visibleUnits = unitList?.filter(filter) ?? [];
  return (
    <>
      {!!visibleUnits.length && (
        <Form.Select
          aria-label={gettext("Source Station")}
          onChange={onChange}
          value={value ?? ""}
        >
          <option value="">Please select a unit</option>
          {visibleUnits.map((unit) => (
            <option key={`unit_picker_${unit.id}`} value={unit.symbol}>
              {unit.symbol}
            </option>
          ))}
        </Form.Select>
      )}
    </>
  );
};

export interface StationMultiSourcePickerProps {
  field: string;
  editedValues: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  filters?: Record<string, (item: any) => boolean>;
}

interface StationMultiSourcePickerRowProps {
  rowIndex: number;
  rowCount: number;
  stationId: string;
  stationSourceMap: Record<string, string[]>;
  filters?: Record<string, (item: any) => boolean>;
  onStationChange: (stationId: string) => void;
  onSourcesChange: (sourceUUIDs: string[]) => void;
  onRemoveRow: () => void;
}

const StationMultiSourcePickerRow: React.FC<
  StationMultiSourcePickerRowProps
> = ({
  rowIndex,
  rowCount,
  stationId,
  stationSourceMap,
  filters,
  onStationChange,
  onSourcesChange,
  onRemoveRow,
}) => {
  const { data: dataSourceData } = useQuery<
    GetDatasourcesByStation,
    GetDatasourcesByStationVariables
  >(GET_DATASOURCES_BY_STATION, {
    variables: {
      stationId: Number(stationId),
    },
    skip: !stationId,
  });

  const selectedSourceUUIDs = stationSourceMap?.[stationId] ?? [];

  const datasourceOptions = React.useMemo(() => {
    const datasourceFilter = filters?.datasource ?? (() => true);
    const list = dataSourceData?.datasource?.filter(datasourceFilter) ?? [];
    return sortByTranslatedKey(list, "name").map((datasource) => ({
      value: String(datasource.source_uuid),
      label: datasource.name ?? gettext("Unnamed"),
      sublabel: datasource.unit?.symbol,
    }));
  }, [dataSourceData, filters]);

  return (
    <>
      <tr>
        <th colSpan={2}>
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">
              {gettext("Station")} #{rowIndex + 1}
            </h5>
            {rowCount > 1 && (
              <Button variant="link" size="sm" onClick={onRemoveRow}>
                {gettext("Remove")}
              </Button>
            )}
          </div>
        </th>
      </tr>
      <tr>
        <th>{gettext("Source Station")}</th>
        <td style={{ width: "100%" }}>
          <SearchableStationPicker
            onChange={onStationChange}
            value={stationId}
          />
        </td>
      </tr>
      {stationId && (
        <tr>
          <th>{gettext("Data Sources")}</th>
          <td style={{ width: "100%" }}>
            <SearchableCheckboxDropdown
              placeholder={gettext("Select data sources...")}
              values={selectedSourceUUIDs}
              options={datasourceOptions}
              onChange={onSourcesChange}
              useFixedPositioning={true}
              summaryFormatter={(count, labels) => {
                if (count === 1) {
                  return labels[0] ?? gettext("1 data source selected");
                }
                return gettext("%1 data sources selected", String(count));
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
};

export const StationMultiSourcePicker: React.FC<
  StationMultiSourcePickerProps
> = ({ field, editedValues, setEditedValues, filters }) => {
  const { metaData } = editedValues ?? {};
  const selectedStationIds: string[] = (metaData?.selectedStationIds ?? []).map(
    (stationId: any) => String(stationId),
  );
  const stationSourceMap: Record<string, string[]> =
    metaData?.stationSourceMap ?? {};

  // Always show at least one row, even before any station is selected
  const rows: string[] =
    selectedStationIds.length > 0 ? selectedStationIds : [""];

  const updateMetaData = (
    newStationIds: string[],
    newStationSourceMap: Record<string, string[]>,
  ) => {
    // Flatten the per-station selections into a single deduplicated list,
    // which is what the widget renderers consume via metaData[field].
    const flattenedSourceUUIDs: string[] = [];
    newStationIds.forEach((stationId) => {
      if (!stationId) {
        return;
      }
      (newStationSourceMap[stationId] ?? []).forEach((sourceUUID) => {
        if (!flattenedSourceUUIDs.includes(sourceUUID)) {
          flattenedSourceUUIDs.push(sourceUUID);
        }
      });
    });
    setEditedValues({
      ...editedValues,
      metaData: {
        ...editedValues.metaData,
        selectedStationIds: newStationIds,
        stationSourceMap: newStationSourceMap,
        [field]: flattenedSourceUUIDs,
      },
    });
  };

  const handleStationChange = (rowIndex: number, newStationId: string) => {
    const newStationIds = [...rows];
    const oldStationId = rows[rowIndex];
    newStationIds[rowIndex] = newStationId;
    const newStationSourceMap = { ...stationSourceMap };
    if (oldStationId && oldStationId !== newStationId) {
      delete newStationSourceMap[oldStationId];
    }
    updateMetaData(newStationIds, newStationSourceMap);
  };

  const handleSourcesChange = (rowIndex: number, newSourceUUIDs: string[]) => {
    const stationId = rows[rowIndex];
    if (!stationId) {
      return;
    }
    updateMetaData(rows, {
      ...stationSourceMap,
      [stationId]: newSourceUUIDs,
    });
  };

  const handleAddRow = () => {
    updateMetaData([...rows, ""], stationSourceMap);
  };

  const handleRemoveRow = (rowIndex: number) => {
    const stationId = rows[rowIndex];
    const newStationIds = rows.filter((_, index) => index !== rowIndex);
    const newStationSourceMap = { ...stationSourceMap };
    if (stationId) {
      delete newStationSourceMap[stationId];
    }
    updateMetaData(newStationIds, newStationSourceMap);
  };

  return (
    <>
      {rows.map((stationId, rowIndex) => (
        <StationMultiSourcePickerRow
          key={`station_multi_source_picker_row_${rowIndex}`}
          rowIndex={rowIndex}
          rowCount={rows.length}
          stationId={stationId}
          stationSourceMap={stationSourceMap}
          filters={filters}
          onStationChange={(newStationId) =>
            handleStationChange(rowIndex, newStationId)
          }
          onSourcesChange={(sourceUUIDs) =>
            handleSourcesChange(rowIndex, sourceUUIDs)
          }
          onRemoveRow={() => handleRemoveRow(rowIndex)}
        />
      ))}
      <tr>
        <td colSpan={2}>
          <Button variant="outline-secondary" size="sm" onClick={handleAddRow}>
            + {gettext("Add another station")}
          </Button>
        </td>
      </tr>
    </>
  );
};

export interface PickerTemplateProps {
  pickerTemplate: string;
  onChange: (e: any) => void;
  field: string;
  value: string;
  editedValues: Record<string, any>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  blankOptionLabel?: string;
  filters?: Record<string, (item: any) => boolean>;
  params?: Record<string, any>;
}
export const PickerTemplate: React.FC<PickerTemplateProps> = ({
  pickerTemplate,
  onChange,
  field,
  value,
  editedValues,
  setEditedValues,
  filters,
  params,
}: PickerTemplateProps) => {
  switch (pickerTemplate) {
    case "station_picker":
      return (
        <SearchableStationPicker
          onChange={(newValue) => onChange({ target: { value: newValue } })}
          value={value}
        />
      );
    case "datasource_picker":
      return (
        <DatasourcePicker
          onChange={onChange}
          value={value}
          editedValues={editedValues}
        />
      );
    case "burrel_camera_picker":
      return <BurrelCameraPicker onChange={onChange} value={value} />;
    case "unit_picker":
      return <UnitPicker onChange={onChange} value={value} filters={filters} />;
    case "flow_source_picker":
      return (
        <FlowSourcePicker
          onChange={onChange}
          value={value}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
          filters={filters}
        />
      );
    case "load_parameters_picker":
      return (
        <LoadParametersPicker
          editedValues={editedValues}
          setEditedValues={setEditedValues}
        />
      );
    case "reporting_frequency_picker":
      return (
        <Form.Select value={value} onChange={onChange}>
          <option value="day">{gettext("Day")}</option>
          <option value="month">{gettext("Month")}</option>
          <option value="quarter">{gettext("Quarter")}</option>
          <option value="year">{gettext("Year")}</option>
        </Form.Select>
      );
    case "show_results_picker":
      return (
        <Form.Select value={value} onChange={onChange}>
          <option value="table">{gettext("Table")}</option>
          <option value="line_graph">{gettext("Line graph")}</option>
          <option value="bar_graph">{gettext("Bar graph")}</option>
        </Form.Select>
      );
    case "manual_lab_data_layout_picker":
      return (
        <Form.Select value={value || "station_dates_rows"} onChange={onChange}>
          <option value="station_dates_rows">
            {gettext(
              "Station name as title, dates as rows, parameters as columns",
            )}
          </option>
          <option value="station_params_rows">
            {gettext(
              "Station name as title, parameters as rows, dates as columns",
            )}
          </option>
          <option value="param_dates_rows">
            {gettext("Parameter as title, dates as rows, stations as columns")}
          </option>
          <option value="param_stations_rows">
            {gettext("Parameter as title, stations as rows, dates as columns")}
          </option>
        </Form.Select>
      );
    case "multi_source_picker":
      return (
        <MultiSourcePicker
          field={field}
          value={value}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
          filters={filters}
          params={params}
        />
      );
    case "multi_station_picker":
      return (
        <MultiStationPicker
          field={field}
          value={value}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
        />
      );
    case "station_multi_source_picker":
      return (
        <StationMultiSourcePicker
          field={field}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
          filters={filters}
        />
      );
    default:
      return null;
  }
};

export const calculateDailyStats = ({
  datapoints,
  startDate,
  endDate,
  ignoreZeroValues = false,
}: {
  datapoints: GetDatasourceData_source_data[];
  startDate: Date;
  endDate: Date;
  ignoreZeroValues?: boolean;
}) => {
  const dailyStats: {
    [key: string]: {
      sum: number;
      count: number;
      min: number;
      max: number;
      invalid: boolean;
    };
  } = {};

  const startDateMidnight = new Date(startDate);
  const endDateMidnight = new Date(endDate);

  startDateMidnight.setHours(0, 0, 0, 0);
  endDateMidnight.setHours(0, 0, 0, 0);
  endDateMidnight.setDate(endDateMidnight.getDate() + 1);

  datapoints.forEach((datapoint) => {
    // Skip invalid datapoints
    if (datapoint.invalid) return;
    if (ignoreZeroValues && datapoint.value === 0) return;

    const date = new Date(datapoint.timestamp);
    if (date < startDateMidnight || date >= endDateMidnight) return;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateKey = `${year}-${month}-${day}`;

    if (!dailyStats[dateKey]) {
      dailyStats[dateKey] = {
        sum: 0,
        count: 0,
        min: Infinity,
        max: -Infinity,
        invalid: false,
      };
    }

    dailyStats[dateKey].sum += datapoint.value;
    dailyStats[dateKey].count += 1;
    dailyStats[dateKey].min = Math.min(
      dailyStats[dateKey].min,
      datapoint.value,
    );
    dailyStats[dateKey].max = Math.max(
      dailyStats[dateKey].max,
      datapoint.value,
    );
  });

  return dailyStats;
};

export const sortDailyAverageKeyList = (dailyAverageKeyList: string[]) => {
  // Create a copy of the list to avoid mutating the original
  const sortedList = [...dailyAverageKeyList];

  // Sort the list in descending order based on date
  sortedList.sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateB.getTime() - dateA.getTime();
  });

  return sortedList;
};

export const formatDailyAverages = (dailyStats: {
  [key: string]: {
    sum: number;
    count: number;
    min: number;
    max: number;
    invalid: boolean;
  };
}) => {
  const dailyAverages: { [key: string]: number } = {};
  const dailyMins: { [key: string]: number } = {};
  const dailyMaxs: { [key: string]: number } = {};

  Object.entries(dailyStats).forEach(([dateKey, stats]) => {
    dailyAverages[dateKey] = stats.sum / stats.count;
    dailyMins[dateKey] = stats.min;
    dailyMaxs[dateKey] = stats.max;
  });

  const dailyAverageKeys = sortDailyAverageKeyList(Object.keys(dailyAverages));

  return { dailyAverageKeys, dailyAverages, dailyMins, dailyMaxs };
};

export const formatAsDatasourceData = (dailyStats: {
  [key: string]: {
    sum: number;
    count: number;
    min: number;
    max: number;
    invalid: boolean;
  };
}): GetDatasourceData_source_data[] => {
  return Object.entries(dailyStats).map(([date, stats]) => ({
    __typename: "data",
    timestamp: date,
    value: stats.sum / stats.count,
    invalid: stats.invalid,
  }));
};
